// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { InsightsResponse } from "@bitebalance/contracts"
import { useI18n, formatEurFromCents } from "@/i18n"
import { EstimatedBadge, EmptyState } from "@/components/state"

/**
 * T21 (ADR-070) — Insights answers one question, once.
 *
 * The page used to be a value card, a forecast block and a weigh-in section
 * stacked in the order they were built, and the first T21 proposal replaced
 * that with five near-equal metrics — which is dashboard density wearing
 * different clothes. Founder review, 2026-09-10: **one answer, one proof, one
 * disclosure.**
 *
 * This is the answer: the realized figure with the product's own Estimated
 * marker, and adherence demoted to its clause. Both numbers are the server's;
 * nothing is computed here. The honest empty state is unchanged — with no
 * confirmed outcomes it renders the same EmptyState `ValueCard` does, and
 * never €0 as a fact.
 */
export function InsightsHeadline({ insights }: { insights: InsightsResponse | null }) {
  const { t, locale } = useI18n()

  if (!insights || !insights.hasData || !insights.realized) {
    return <EmptyState title={t("views.valueCard.emptyTitle")} body={t("views.valueCard.emptyBody")} />
  }

  return (
    <div className="anim-rise">
      <p className="flex flex-wrap items-center gap-3">
        <span className="font-display text-5xl leading-none font-semibold text-green">
          {formatEurFromCents(insights.realized.amountCents, locale)}
        </span>
        <EstimatedBadge />
      </p>
      {insights.adherence && (
        <p className="mt-3 max-w-prose text-sm text-ink-2">
          {t("views.valueCard.headlineClause", {
            recorded: insights.adherence.outcomesRecorded,
            planned: insights.adherence.mealsPlanned,
          })}
        </p>
      )}
    </div>
  )
}

const KIND_LABEL_KEYS: Record<string, string> = {
  pantry_reuse: "views.valueCard.byKindPantryReuse",
  offer: "views.valueCard.byKindOffer",
}

/**
 * The supporting half of the same answer — the projected figure and the
 * per-kind split of what was realized. Real, earned data, so it is never
 * deleted; it moves behind the disclosure with the budget forecast, because
 * "where exactly did it come from" is not the question the page opens with.
 */
export function InsightsDetail({ insights }: { insights: InsightsResponse | null }) {
  const { t, locale } = useI18n()
  if (!insights || !insights.hasData) return null
  const kinds = Object.entries(insights.byKind ?? {}).filter(
    ([, cents]) => typeof cents === "number" && cents > 0
  )
  if (!insights.projected && kinds.length === 0) return null

  return (
    <div className="flex flex-col gap-3">
      {insights.projected && (
        <div>
          <p className="text-xs text-ink-2 uppercase">{t("views.valueCard.projectedLabel")}</p>
          <p className="flex items-center gap-2 text-lg font-semibold text-ink">
            {formatEurFromCents(insights.projected.amountCents, locale)}
            <EstimatedBadge />
          </p>
        </div>
      )}
      {kinds.length > 0 && (
        <div>
          <p className="text-xs text-ink-2 uppercase">{t("views.valueCard.byKindHeading")}</p>
          <ul className="mt-1 flex flex-col gap-0.5">
            {kinds.map(([kind, cents]) => (
              <li key={kind} className="flex justify-between text-sm text-ink-2">
                <span>{t(KIND_LABEL_KEYS[kind] ?? kind)}</span>
                <span className="tabular-nums">{formatEurFromCents(cents as number, locale)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
